import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { SwitchEduButtonComponent } from 'src/app/core/components/switch-edu-button/switch-edu-button.component';
import { AppConfigService } from 'src/app/core/services/app-config.service';
import { environment } from 'src/environments/environment';

import { ZitadelStartExtAuthResponseModel } from '../../models/zitadel-start-ext-auth-response.model';
import { ZitadelStartExtAuthModel } from '../../models/zitadel-start-ext-auth.model';
import { ExternalAuthService } from '../../services/external-auth.service';
import { SsoService } from '../../services/sso.service';
import { LegacyLoginCardComponent } from '../legacy-login-card/legacy-login-card.component';
import { LoadingIndicatorComponent } from '@premotec/pmt-bootstrap';

@Component({
  selector: 'app-login-provider-selector',
  templateUrl: './login-provider-selector.component.html',
  standalone: true,
  imports: [
    TranslocoDirective,
    SwitchEduButtonComponent,
    LegacyLoginCardComponent,
    LoadingIndicatorComponent
  ],
  styleUrl: './login-provider-selector.component.scss'
})
export class LoginProviderSelectorComponent implements OnInit, OnDestroy {
  protected isCheckingSsoSession = signal(false);

  protected isProcessingSwitchEduLogin = signal(false);
  protected isProcessingLegacyLogin = signal(false);
  protected hasLoginError = signal(false);

  /** True while the login was started automatically instead of by a click on a provider. */
  protected isAutoRedirecting = signal(false);


  private returnUrl!: string;
  private pageShowListener?: (event: PageTransitionEvent) => void;

  private readonly route = inject(ActivatedRoute);
  private readonly externalAuthService = inject(ExternalAuthService);
  private readonly ssoService = inject(SsoService);
  private readonly appConfigService = inject(AppConfigService);

  protected readonly isIdpSelectionActive = this.appConfigService.isIdpSelectionActive;

  public ngOnInit(): void {
    this.handlePageShow();

    this.returnUrl = this.setSuccessUrl();



    this.route.queryParamMap.subscribe((params) => {
      const authRequest = params.get('authRequest');
      if (authRequest != null) {
        sessionStorage.setItem('authRequest', authRequest);

        // Check for existing SSO session before showing IDP selection
        this.checkSsoSession(authRequest);
      } else {
        // redirect to onlineDesk if env is not development
        if (environment.staging || environment.production) {
          window.location.href = environment.onlinedeskUrl;
        } else {
          // no authRequest locally: continue as if one was present, the sso check needs an id
          this.proceedWithNormalFlow();
        }
      }
    });
  }

  /**
   * Check if user has an existing valid SSO session.
   * If yes, complete login automatically without IDP redirect.
   */
  private checkSsoSession(authRequestId: string): void {
    this.isCheckingSsoSession.set(true);
    this.ssoService.checkSsoSession(authRequestId).subscribe({
      next: (response) => {
        if (response.hasValidSession && response.callbackUrl) {
          // SSO session is valid, redirect to callback URL directly
          sessionStorage.removeItem('authRequest');
          window.location.href = response.callbackUrl;
        } else {
          // No valid SSO session, proceed with normal login flow
          this.isCheckingSsoSession.set(false);
          this.proceedWithNormalFlow();
        }
      },
      error: () => {
        // SSO check failed, proceed with normal login flow
        this.isCheckingSsoSession.set(false);
        this.proceedWithNormalFlow();
      }
    });
  }

  private proceedWithNormalFlow(): void {
    const loginType = this.route.snapshot.queryParamMap.get('loginType');
    if (loginType === 'switchEduId') {
      this.isAutoRedirecting.set(true);
      this.processSwitchEduIdLogin(false);
    } else if (!this.isIdpSelectionActive()) {
      this.isAutoRedirecting.set(true);
      this.processLegacyLogin(false);
    }
  }

  protected processLegacyLogin(forceLogin: boolean): void {
    this.isProcessingLegacyLogin.set(true);
    this.hasLoginError.set(false);
    const model: ZitadelStartExtAuthModel = {
      idpId: environment.externalLogin.idpIds.legacyId,
      successUrl: this.returnUrl,
      failureUrl: environment.externalLogin.failureUrl,
      forceLogin
    };

    this.externalAuthService.getLoginUrl(model).subscribe({
      next: (returnModel: ZitadelStartExtAuthResponseModel) => {
        window.location.href = returnModel.authUrl;
      },
      error: () => {
        this.isProcessingLegacyLogin.set(false);
        this.isAutoRedirecting.set(false);
        this.hasLoginError.set(true);
      }
    });
  }

  protected processSwitchEduIdLogin(forceLogin: boolean): void {
    this.isProcessingSwitchEduLogin.set(true);
    this.hasLoginError.set(false);
    this.externalAuthService.redirectToSwitchEduIdLogin(this.returnUrl, forceLogin).subscribe({
      error: () => {
        this.isProcessingSwitchEduLogin.set(false);
        this.isAutoRedirecting.set(false);
        this.hasLoginError.set(true);
      }
    });
  }

  private setSuccessUrl(): string {
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    const baseUrl = window.location.origin;
    return returnUrl ?? baseUrl.concat(environment.externalLogin.successUrl);
  }

  /**
   * Handle pageshow event to reset loading states when user navigates back via browser back button.
   * The pageshow event fires when a page is loaded from bfcache (back/forward cache).
   */
  private handlePageShow(): void {
    const resetLoadingStates = (event: PageTransitionEvent): void => {
      // persisted=true means the page was loaded from bfcache (browser back button)
      if (event.persisted) {
        this.isProcessingSwitchEduLogin.set(false);
        this.isProcessingLegacyLogin.set(false);
        this.isCheckingSsoSession.set(false);
        this.isAutoRedirecting.set(false);
      }
    };

    window.addEventListener('pageshow', resetLoadingStates);
    this.pageShowListener = resetLoadingStates;
  }

  public ngOnDestroy(): void {
    if (this.pageShowListener) {
      window.removeEventListener('pageshow', this.pageShowListener);
    }
  }
}
